import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { usePlayersAdmin } from "@/hooks/api/usePlayers";
import type { Player } from "@/types/api.types";
import { cn } from "@/lib/utils";

export interface PlayerOption {
  id: number;
  name: string;
  imageUrl: string | null;
  nationCode: string | null;
  position: string | null;
  club: string | null;
  clubLogoUrl: string | null;
  jerseyNumber: number | null;
  age: number | null;
}

interface Props {
  value: { name: string; imageUrl?: string | null } | null;
  onChange: (player: PlayerOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

function toOption(p: Player): PlayerOption {
  return {
    id: p.id,
    name: p.name,
    imageUrl: p.photo,
    nationCode: p.nation?.code ?? p.nationCode ?? null,
    position: p.position ?? null,
    club: p.club?.name ?? null,
    clubLogoUrl: p.club?.logoUrl ?? null,
    jerseyNumber: p.jerseyNumber ?? null,
    age: p.age ?? null,
  };
}

export function PlayerSelect({ value, onChange, placeholder = "Select player…", disabled, className }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);

  // `isActive: 'all'` tells the backend to include both active and
  // inactive players so retired legends or dropped-squad players can
  // still be picked as quiz/trivia/daily-challenge answers.
  const { data, isLoading } = usePlayersAdmin({ limit: 500, isActive: 'all' });
  const players = data?.players ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return players;
    return players.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.club?.name?.toLowerCase().includes(q) ||
        p.nation?.code?.toLowerCase().includes(q),
    );
  }, [players, query]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 bg-white text-left text-sm",
          "hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        {value?.imageUrl ? (
          <img src={value.imageUrl} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-6 h-6 rounded-full bg-gray-100 shrink-0" />
        )}
        <span className={cn("flex-1 truncate", !value?.name && "text-gray-400")}>
          {value?.name || placeholder}
        </span>
        {value?.name && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            className="p-1 rounded hover:bg-gray-100"
          >
            <X className="h-3 w-3 text-gray-400" />
          </span>
        )}
        <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search players…"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="p-3 text-sm text-gray-400">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-3 text-sm text-gray-400">No players found</div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { onChange(toOption(p)); setOpen(false); setQuery(""); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                >
                  {p.photo ? (
                    <img src={p.photo} alt="" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gray-100" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{p.name}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {[p.position, p.club?.name, p.nation?.code].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {p.isActive === false && (
                    <span className="text-xs text-gray-400">inactive</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
